import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import HeadMeta from '../components/HeadMeta';
import ScrollReveal from '../components/ScrollReveal';
import { useScrollZoom } from '../hooks/useScrollZoom';

export default function FAQPage() {
  const { lang, t, getLocalized } = useLanguage();
  const [faqs, setFaqs] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const heroZoom = useScrollZoom({ baseScale: 1.1, maxScale: 1.4, zoomSpeed: 0.0006 });

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/v2/content/faqs?sort=order&order=asc')
      .then((res) => res.json())
      .then((data) => {
        if (data.items) setFaqs(data.items);
      })
      .catch((err) => console.error(err));
  }, []);

  const toggleFaq = (idx) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  return (
    <>
      <HeadMeta 
        title={t('faqPage.title') || 'FAQ - Spørgsmål og Svar'} 
        description={t('faqPage.subtitle') || 'Få svar på typiske spørgsmål om booking, betaling, afhentning på hotellet, dansktalende support og ferieplanlægning i Hurghada.'}
      />

      {/* Hero */}
      <section className="pt-28 sm:pt-36 lg:pt-40 pb-16 bg-slate-950 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/hurghada-sunny-hero.jpg"
            alt="FAQ Captain Hurghada"
            style={{
              transform: `scale(${heroZoom.scale}) translate3d(0, ${heroZoom.translateY}px, 0)`,
              transition: 'transform 0.1s cubic-bezier(0.1, 1, 0.1, 1)',
              willChange: 'transform',
            }}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent pointer-events-none" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal variant="up" duration={800}>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-6 uppercase">
              {t('faqPage.title')}
            </h1>
          </ScrollReveal>

          <ScrollReveal variant="up" delay={200} duration={800}>
            <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto leading-relaxed">
              {t('faqPage.subtitle')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ Accordion List */}
      <section className="py-20 bg-[#FAF9F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="space-y-3.5">
            {faqs.map((item, idx) => {
              const rawQuestion = getLocalized(item.question) || '';
              const rawAnswer = getLocalized(item.answer) || '';
              const question = rawQuestion.replace(/^["\s]+|["\s]+$/g, '');
              const answerHtml = rawAnswer.replace(/^["\s]+|["\s]+$/g, '');
              const isOpen = openIndex === idx;

              return (
                <ScrollReveal key={item.id || idx} variant="up" delay={(idx % 6) * 60} duration={600}>
                  <div
                    className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm transition-all hover:border-slate-300"
                  >
                    <button
                      onClick={() => toggleFaq(idx)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between gap-4 font-semibold text-sm sm:text-base text-slate-800 hover:text-slate-950 transition-colors cursor-pointer"
                    >
                      <span>{question}</span>
                      <div className={`shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-cyan-600' : 'text-stone-400'}`}>
                        <i className="fa-solid fa-chevron-down text-sm" aria-hidden="true" />
                      </div>
                    </button>

                    {isOpen && (
                      <div className="px-6 pb-5 pt-1 text-slate-600 text-xs sm:text-sm leading-relaxed border-t border-stone-100 animate-in fade-in duration-200">
                        <div dangerouslySetInnerHTML={{ __html: answerHtml }} />
                      </div>
                    )}
                  </div>
                </ScrollReveal>
              );
            })}
          </div>

          {/* Need more help banner (Matching Live Site) */}
          <div className="mt-16 bg-slate-950 rounded-3xl p-8 sm:p-10 text-white text-center space-y-4 shadow-xl">
            <h3 className="font-bold text-lg sm:text-xl uppercase tracking-wide">
              {t('faqPage.notFoundTitle')}
            </h3>
            <p className="text-slate-300 text-xs sm:text-sm max-w-lg mx-auto leading-relaxed">
              {t('faqPage.notFoundSub')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
              <Link
                to="/kontakt"
                className="px-6 py-2.5 rounded-full bg-cyan-600 hover:bg-cyan-500 text-white font-medium text-xs uppercase tracking-wider transition-colors shadow-md"
              >
                {t('hero.ctaContact')}
              </Link>
              <a
                href="https://www.instagram.com/captainhurgada/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 rounded-full border border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-white font-medium text-xs uppercase tracking-wider transition-colors flex items-center gap-2"
              >
                <i className="fa-brands fa-instagram text-white" aria-hidden="true" />
                <span>Instagram @captainhurgada</span>
              </a>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}
